import { TextField, SxProps, Theme, debounce } from "@mui/material";
import { ChangeEvent, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocalization } from "@fluent/react";
import { processExists } from "../ProjectProcessApiClient";
import { useAppDispatch } from "../../../../app/hooks";

export interface ProcessNumberFieldProps {
    value: string;
    onChange: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    onValidationResult?: (valid: boolean) => void;
    structureCode?: string | null;
    autoSuggest?: boolean;
    tryIncrement?: boolean;
    maxTries?: number;
    sx?: SxProps<Theme>;
    label?: ReactNode;
    fullWidth?: boolean;
    required?: boolean;
}

type Status = undefined | "checking" | "ok" | "exists";

const sanitizeCode = (code?: string | null) =>
    (code ?? "GEN").toString().trim().replace(/\s+/g, "-").toUpperCase();

const buildBase = (structureCode?: string | null) => {
    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, "0");
    const code = sanitizeCode(structureCode);
    return `P-${yyyy}-${mm}-${code}-VAL`;
};

export default function ProcessNumberField(props: ProcessNumberFieldProps) {
    const {
        sx,
        value,
        onChange,
        onValidationResult,
        structureCode,
        autoSuggest = true,
        required = true,
        fullWidth,
        label = "Numer procesu",
    } = props;

    const dispatch = useAppDispatch();
    const { l10n } = useLocalization();
    const [status, setStatus] = useState<Status>(undefined);

    const instantCheck = useCallback(async (raw: string) => {
        const processNumber = raw.trim();
        if (!processNumber) {
            setStatus(undefined);
            onValidationResult?.(false);
            return;
        }
        onValidationResult?.(false);
        setStatus("checking");
        try {
            const res = await dispatch(processExists(processNumber)).unwrap();
            const exists = !!res?.exists;
            const valid = !exists;
            setStatus(valid ? "ok" : "exists");
            onValidationResult?.(valid);
        } catch {
            setStatus(undefined);
            onValidationResult?.(false);
        }
    }, [dispatch, onValidationResult]);

    const debouncedCheck = useMemo(
        () =>
            debounce(async (raw: string) => {
                const processNumber = raw.trim();
                if (!processNumber) {
                    setStatus(undefined);
                    onValidationResult?.(false);
                    return;
                }

                setStatus("checking");
                try {
                    const res = await dispatch(processExists(processNumber)).unwrap();
                    const exists = !!res?.exists;
                    const valid = !exists;
                    setStatus(valid ? "ok" : "exists");
                    onValidationResult?.(valid);
                } catch {
                    setStatus(undefined);
                    onValidationResult?.(false);
                }
            }, 600),
        [dispatch, onValidationResult]
    );

    useEffect(() => {
        return () => {
            debouncedCheck.clear?.();
        };
    }, [debouncedCheck]);

    useEffect(() => {
        if (!autoSuggest) return;
        if (value?.trim()) return;

        const base = buildBase(structureCode);
        const evt = { target: { value: base } } as ChangeEvent<HTMLInputElement>;
        onChange(evt);
        debouncedCheck(base);
    }, [autoSuggest, structureCode, value, onChange, debouncedCheck]);

    const didMountRef = useRef(false);
    useEffect(() => {
        if (didMountRef.current) return;
        didMountRef.current = true;

        if (value?.trim()) {
            instantCheck(value);
        }
    }, [value, instantCheck]);

    useEffect(() => {
        if (!value?.trim()) {
            setStatus(undefined);
            onValidationResult?.(false);
        }
    }, [value, onValidationResult]);

    const handleChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        onChange(e);
        debouncedCheck(e.target.value);
    }, [onChange, debouncedCheck]);

    const error = (required ? !value.trim() : false) || status === "exists";

    let helperText: ReactNode = " ";
    if (status === "checking") helperText = l10n.getString("process-number-checking", undefined, "Sprawdzam…");
    if (status === "exists") helperText = l10n.getString("process-number-exists", undefined, "Taki numer procesu już istnieje");

    return (
        <TextField
            sx={sx}
            value={value}
            onChange={handleChange}
            error={error}
            helperText={helperText}
            required={required}
            fullWidth={fullWidth}
            label={label}
        />
    );
}
